"use client";

import { useCallback, useMemo, useState } from "react";
import type {
  DegreeOfAbstraction,
  DegreeOfImpliciteness,
  DegreeOfIntention,
  ExtractThoughtsRequest,
  MeiliOrigin,
  MindstringPrototype,
  SentenceStructure,
  Situation,
  SupportedLanguage,
} from "@/generated/fastapi";
import {
  countSituationsByPrototype,
  useAssignableSituations,
  useSituationsQuery,
} from "@/features/situations";
import { useSystemDesign } from "@/shared/system-design";
import { showErrorToast } from "@/shared/feedback";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import {
  isVisibleMindstringPrototype,
  mindstringPrototypesSlang,
} from "@/shared/config/backend";
import { useThoughtRefresh } from "@/features/thoughts";
import { useSourceExtraction } from "./useSourceExtraction";
import { useAuth, useGlobalStore } from "@/state/globalStore";

type UseSourceExtractionControllerParams = {
  allowUnanchoredExtraction?: boolean;
  meiliOrigin: MeiliOrigin;
  onUpdateSearchResultAction?: (updatedOrigin: MeiliOrigin) => void;
  presetSituation?: Situation | null | undefined;
  presetLanguage?: SupportedLanguage | null | undefined;
  presetPrototype?: MindstringPrototype | null | undefined;
  skipFetchSuggestedThoughts?: boolean;
};

function getMiddleIndex<T>(items: T[]): number {
  return Math.floor(items.length / 2);
}

function formatSituationLabel(situation: Situation | null | undefined) {
  if (!situation) {
    return "Choose situation";
  }

  const contextLabel = [situation.position, situation.context]
    .filter(Boolean)
    .join(", ");

  return situation.abbr || contextLabel || "Chosen situation";
}

function formatProcessedStatus(status: MeiliOrigin["processed_status"]) {
  return status === "PROCESSED" ? "Source shaped" : "Source material";
}

function formatSourceScopeLabel(originId: string) {
  return `Source ${originId.slice(0, 8)}`;
}

function formatOriginPreview(rawTextInput: string) {
  const normalizedText = rawTextInput.trim();

  if (!normalizedText) {
    return "No source preview available.";
  }

  return normalizedText;
}

function getExtractionDisabledReason({
  allowUnanchoredExtraction,
  assignableSituationsCount,
  isLoadingSituations,
  selectedLanguage,
  selectedTargetSituation,
  selectedPrototypeIsVisible,
  targetSupportsPrototype,
}: {
  allowUnanchoredExtraction: boolean;
  assignableSituationsCount: number;
  isLoadingSituations: boolean;
  selectedLanguage: SupportedLanguage | null;
  selectedTargetSituation: Situation | null;
  selectedPrototypeIsVisible: boolean;
  targetSupportsPrototype: boolean;
}) {
  if (isLoadingSituations) {
    return "Loading situations...";
  }

  if (!selectedPrototypeIsVisible) {
    return "This perspective is available to admins only.";
  }

  if (!selectedLanguage) {
    return "Choose the language for the suggested thoughts.";
  }

  if (selectedTargetSituation && !targetSupportsPrototype) {
    return "This situation does not accept the selected perspective.";
  }

  if (selectedTargetSituation || allowUnanchoredExtraction) {
    return null;
  }

  return assignableSituationsCount === 0
    ? "No situation accepts the selected perspective."
    : "Choose the situation that should anchor these suggested thoughts.";
}

export default function useSourceExtractionController({
  allowUnanchoredExtraction = true,
  meiliOrigin,
  onUpdateSearchResultAction,
  presetSituation,
  presetLanguage,
  presetPrototype,
  skipFetchSuggestedThoughts = false,
}: UseSourceExtractionControllerParams) {
  const { isMobileDevice } = useSystemDesign();
  const { isUserAdmin } = useAuth();
  const activePrototypeCategory = useGlobalStore(
    (state) => state.activePrototypeCategory,
  );
  const {
    mindstringPrototypesSlangItems,
    languageItems,
    degreeOfImplicitenessItems,
    degreeOfAbstractionItems,
    degreeOfIntentionItems,
    sentenceStructureItems,
  } = useOptionItems();
  const situationsQuery = useSituationsQuery();
  const { waitingForExtraction, handleSourceExtractionClick } =
    useSourceExtraction();
  const { syncSuggestedThoughtMutation } = useThoughtRefresh();

  const [targetSituation, setTargetSituation] = useState<Situation[]>(
    presetSituation ? [presetSituation] : [],
  );
  const [targetLanguage, setTargetLanguage] = useState<SupportedLanguage[]>(
    presetLanguage
      ? [presetLanguage]
      : [meiliOrigin.language as SupportedLanguage],
  );
  const [extractPrototype, setExtractPrototype] = useState<MindstringPrototype>(
    presetPrototype ?? activePrototypeCategory,
  );
  const [sentenceStructure, setSentenceStructure] = useState<
    SentenceStructure[]
  >(["full_sentence"]);
  const defaultDegreeOfImpliciteness =
    degreeOfImplicitenessItems[getMiddleIndex(degreeOfImplicitenessItems)]
      ?.value;
  const defaultDegreeOfAbstraction =
    degreeOfAbstractionItems[getMiddleIndex(degreeOfAbstractionItems)]?.value;
  const defaultDegreeOfIntention =
    degreeOfIntentionItems[getMiddleIndex(degreeOfIntentionItems)]?.value;
  const [degreeOfImpliciteness, setDegreeOfImpliciteness] =
    useState<DegreeOfImpliciteness>(defaultDegreeOfImpliciteness);
  const [degreeOfAbstraction, setDegreeOfAbstraction] =
    useState<DegreeOfAbstraction>(defaultDegreeOfAbstraction);
  const [degreeOfIntention, setDegreeOfIntention] =
    useState<DegreeOfIntention>(defaultDegreeOfIntention);
  const [isExtractionOptionsOpen, setIsExtractionOptionsOpen] = useState(false);
  const [extractionRunCount, setExtractionRunCount] = useState(0);

  const selectedPrototype = presetPrototype ?? extractPrototype;
  const selectedPrototypeIsVisible = isVisibleMindstringPrototype(
    selectedPrototype,
    isUserAdmin,
  );
  const selectedPrototypes = useMemo(
    () => [selectedPrototype],
    [selectedPrototype],
  );
  const { items: situationItems, situations: assignableSituations } =
    useAssignableSituations(situationsQuery.data, selectedPrototypes);
  const situationCountsByPrototype = useMemo(
    () => countSituationsByPrototype(situationsQuery.data),
    [situationsQuery.data],
  );
  const prototypeItemsWithCounts = useMemo(
    () =>
      mindstringPrototypesSlangItems.map((item) => ({
        ...item,
        count: situationCountsByPrototype[item.value],
      })),
    [mindstringPrototypesSlangItems, situationCountsByPrototype],
  );
  const situationSelectorKey = selectedPrototype;
  const selectedPrototypeLabel =
    mindstringPrototypesSlang[selectedPrototype]?.label ?? selectedPrototype;
  const selectedTargetSituation = targetSituation[0] ?? null;
  const selectedLanguage = targetLanguage[0] ?? null;
  const defaultLanguage = presetLanguage ?? meiliOrigin.language ?? null;
  const targetSituationLabel = formatSituationLabel(selectedTargetSituation);
  const targetSupportsPrototype = selectedTargetSituation
    ? selectedTargetSituation.prototypes.includes(selectedPrototype)
    : true;
  const canExtract = Boolean(
    (selectedTargetSituation || allowUnanchoredExtraction) &&
      selectedLanguage &&
      selectedPrototypeIsVisible &&
      targetSupportsPrototype,
  );
  const extractionDisabledReason = getExtractionDisabledReason({
    allowUnanchoredExtraction,
    assignableSituationsCount: assignableSituations.length,
    isLoadingSituations: situationsQuery.isLoading,
    selectedLanguage,
    selectedTargetSituation,
    selectedPrototypeIsVisible,
    targetSupportsPrototype,
  });
  const unanchoredExtractionWarning =
    allowUnanchoredExtraction && !selectedTargetSituation
      ? "Suggestions will need a situation before they can be confirmed into your profile. Consider choosing a situation to anchor them right away."
      : null;
  const hasCustomOptions = Boolean(
    (selectedLanguage ?? null) !== defaultLanguage ||
      sentenceStructure[0] !== "full_sentence" ||
      degreeOfImpliciteness !== defaultDegreeOfImpliciteness ||
      degreeOfAbstraction !== defaultDegreeOfAbstraction ||
      degreeOfIntention !== defaultDegreeOfIntention,
  );

  const extractRequest = useMemo<ExtractThoughtsRequest>(
    () => ({
      situation_id: selectedTargetSituation ? selectedTargetSituation.id : null,
      selected_language: selectedLanguage
        ? selectedLanguage
        : ("MUST_BE_SELECTED" as SupportedLanguage),
      selected_prototype: selectedPrototype,
      sourced_where: "manage_thoughts_page",
      origin_purpose: "mindstring_extraction",
      origin_id: meiliOrigin.objectID,
      flag_as_processed: true,
      degree_of_impliciteness: degreeOfImpliciteness,
      degree_of_abstraction: degreeOfAbstraction,
      degree_of_intention: degreeOfIntention,
      sentence_structure: sentenceStructure[0],
    }),
    [
      degreeOfAbstraction,
      degreeOfImpliciteness,
      degreeOfIntention,
      meiliOrigin.objectID,
      selectedLanguage,
      selectedPrototype,
      selectedTargetSituation,
      sentenceStructure,
    ],
  );

  const drawerInfoItems = useMemo(
    () => [
      {
        label: formatProcessedStatus(meiliOrigin.processed_status),
        colorPalette:
          meiliOrigin.processed_status === "PROCESSED" ? "green" : "orange",
      },
      {
        label: targetSituationLabel,
        colorPalette: selectedTargetSituation ? "blue" : "gray",
      },
      { label: selectedPrototypeLabel, colorPalette: "purple" },
      {
        label: selectedLanguage ?? "Choose language",
        colorPalette: selectedLanguage ? "teal" : "gray",
      },
      ...(skipFetchSuggestedThoughts
        ? []
        : [
            {
              label: "Review suggestions",
              colorPalette: "blue" as const,
            },
          ]),
    ],
    [
      meiliOrigin.processed_status,
      selectedLanguage,
      selectedPrototypeLabel,
      selectedTargetSituation,
      skipFetchSuggestedThoughts,
      targetSituationLabel,
    ],
  );
  const reviewScopeItems = useMemo(
    () => [
      {
        label: formatSourceScopeLabel(meiliOrigin.objectID),
        colorPalette: "blue" as const,
      },
      {
        label: selectedPrototypeLabel,
        colorPalette: "purple" as const,
      },
      ...(selectedTargetSituation
        ? [
            {
              label: targetSituationLabel,
              colorPalette: "teal" as const,
            },
          ]
        : []),
    ],
    [
      meiliOrigin.objectID,
      selectedPrototypeLabel,
      selectedTargetSituation,
      targetSituationLabel,
    ],
  );

  const handleExtract = useCallback(async () => {
    if (extractionDisabledReason) {
      showErrorToast(extractionDisabledReason);
      return;
    }

    const extractionResult = await handleSourceExtractionClick({
      request: extractRequest,
      onSuccessExtractionAction: () => {
        setIsExtractionOptionsOpen(false);
      },
    });

    if (extractionResult?.status === "success") {
      setExtractionRunCount((currentCount) => currentCount + 1);
      syncSuggestedThoughtMutation(selectedPrototype);
      onUpdateSearchResultAction?.({
        ...meiliOrigin,
        processed_status: "PROCESSED",
      });
    }
  }, [
    extractRequest,
    extractionDisabledReason,
    handleSourceExtractionClick,
    meiliOrigin,
    onUpdateSearchResultAction,
    selectedPrototype,
    syncSuggestedThoughtMutation,
  ]);

  const handleToggleExtractionOptions = useCallback(() => {
    setIsExtractionOptionsOpen((isOpen) => !isOpen);
  }, []);

  const handleSelectedPrototypeChange = useCallback(
    (nextPrototype: MindstringPrototype) => {
      setExtractPrototype(nextPrototype);

      const currentTargetSituation = targetSituation[0];
      if (
        !presetSituation &&
        currentTargetSituation &&
        !currentTargetSituation.prototypes.includes(nextPrototype)
      ) {
        setTargetSituation([]);
      }
    },
    [presetSituation, targetSituation],
  );

  return {
    drawerInfoItems,
    originPreview: formatOriginPreview(meiliOrigin.raw_text_input),
    extraction: {
      canExtract,
      disabledReason: extractionDisabledReason,
      hasCustomOptions,
      allowUnanchoredExtraction,
      handleExtract,
      handleToggleExtractionOptions,
      isExtracting: waitingForExtraction,
      hasRunExtraction: extractionRunCount > 0,
      isOptionsOpen: isExtractionOptionsOpen,
      selectedPrototype,
      setSelectedPrototype: handleSelectedPrototypeChange,
      setOptionsOpen: setIsExtractionOptionsOpen,
      selectedPrototypeLocked: Boolean(presetPrototype),
      isLoadingSituations: situationsQuery.isLoading,
      situationSelectorKey,
      situationItems,
      situations: assignableSituations,
      targetSituation,
      setTargetSituation,
      targetSituationLocked: Boolean(presetSituation),
      unanchoredExtractionWarning,
    },
    options: {
      degreeOfAbstraction,
      degreeOfAbstractionItems,
      degreeOfImpliciteness,
      degreeOfImplicitenessItems,
      degreeOfIntention,
      degreeOfIntentionItems,
      languageItems,
      sentenceStructure,
      sentenceStructureItems,
      setDegreeOfAbstraction,
      setDegreeOfImpliciteness,
      setDegreeOfIntention,
      setSentenceStructure,
      setTargetLanguage,
      targetLanguage,
      targetLanguageInitValue: presetLanguage
        ? presetLanguage
        : meiliOrigin.language
          ? (meiliOrigin.language as SupportedLanguage)
          : undefined,
    },
    review: {
      scopeItems: reviewScopeItems,
    },
    ui: {
      isMobileDevice,
      prototypeItems: prototypeItemsWithCounts,
    },
  };
}
